import { useEffect } from 'react';
import { useMetaTags } from 'react-metatags-hook';
import { JsonLd } from 'react-schemaorg';
import { standardFilter } from '../../_flux/defaults';
import { type Car, type Filter } from '../../_types/Car';
import { useAllFacetsContext } from '../../context/all-facets';
import { useSettingsContext } from '../../context/settings';
import useLocales, { getMetaLocales } from '../../hooks/useLocales';
import { Modules } from '../../modules/_definition/types';
import getUrlFromFilter from '../../modules/fahrzeugsuche/filter/utils/getUrlFromFilter';
import generateTitle from '../../utils/texte/generateTitle';
import getCarMeta from '../../utils/texte/getCarMeta';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import checkUndefinedOrNull from '../../utils/tools/checkUndefinedOrNull';
import getChangedKeysByObjects from '../../utils/tools/getChangedKeysByObjects';
import { type MetaData } from '../../vars';
import ldJsonCar from './vehicle-schemata/car';

type MetaProps = {
    module: Modules;
    noLimit?: boolean;
} & (
    | {
          desc?: string;
          tags?: string;
          title?: string;
          type: 'common';
      }
    | {
          desc?: string;
          isFiltered?: boolean;
          filter?: Filter;
          tags?: string;
          title?: { withPage: string; withoutPage: string };
          type: 'list';
      }
    | {
          car?: Car<false>;
          loading: boolean;
          type: 'car';
      }
);

let timerH1: NodeJS.Timeout;

export default function Meta(props: MetaProps) {
    const { module, noLimit, type } = props;
    const allFacets = useAllFacetsContext();
    const settings = useSettingsContext();
    const { translate } = useLocales();

    const meta: MetaData = { canonical: undefined, desc: undefined, image: undefined, imageAlt: undefined, tags: undefined, title: undefined, titleRaw: undefined, url: undefined };
    if (type === 'common') {
        const { desc, tags, title } = props;
        meta.desc = desc?.substring(0, noLimit ? undefined : 249);
        meta.tags = tags;
        meta.title = generateTitle(title, noLimit, settings);
        meta.titleRaw = title;
        meta.url = location?.href;
    } else if (type === 'list') {
        const { desc, filter, tags, title } = props;
        meta.desc = desc?.substring(0, noLimit ? undefined : 249);
        meta.tags = tags;
        meta.title = generateTitle(title?.withPage, noLimit, settings);
        meta.titleRaw = title?.withoutPage;
        meta.url = location?.href;

        if (settings?.general?.useCanonicalLinks === true) {
            const rawCanonical = getUrlFromFilter(allFacets, settings, filter, module, true);
            meta.canonical = `${rawCanonical?.origin}${settings?.general?.searchPath}`; // canonical url default and for pages with no subpages when filtered

            const changedFilters = getChangedKeysByObjects(standardFilter, filter);
            if (
                !checkUndefinedNullOrEmpty(changedFilters) &&
                changedFilters?.filter(
                    (key) =>
                        key !== 'hersteller0' &&
                        key !== 'modell0' &&
                        key !== 'akttypList' &&
                        key !== (settings?.general?.useFirmenFilter ? 'idFirma' : 'Standort') &&
                        key !== 'ignoreFirma' &&
                        key !== 'ignoreStandort' &&
                        key !== 'StandortQuelle' &&
                        key !== 'nfzHersteller' &&
                        key !== 'NFZ' &&
                        key !== 'order' &&
                        key !== 'moreCars' &&
                        key !== 'pageSize' &&
                        key !== 'sort'
                )?.length === 0
            ) {
                meta.canonical = `${rawCanonical?.origin}${rawCanonical?.url}`;
            }
        }
    } else {
        const { car, loading } = props;
        const carMeta = getCarMeta(translate, car, settings, module, loading);
        meta.canonical = carMeta?.canonical;
        meta.desc = carMeta?.desc?.substring(0, noLimit ? undefined : 249);
        meta.image = carMeta?.image;
        meta.imageAlt = carMeta?.imageAlt;
        meta.tags = carMeta?.tags;
        meta.title = generateTitle(carMeta?.title, noLimit, settings);
        meta.titleRaw = carMeta?.titleRaw;
        meta.url = carMeta?.url;
    }

    const color = settings?.style?.primaryColor;

    useMetaTags(
        {
            title: meta?.title,
            description: meta?.desc,
            metas: [
                !checkUndefinedNullOrEmpty(meta?.tags) ? { name: 'keywords', content: meta?.tags } : undefined,
                !settings?.general?.ahdWeb ? { name: 'msapplication-TileColor', content: color } : undefined,
                !settings?.general?.ahdWeb ? { name: 'theme-color', content: color } : undefined,
            ]?.filter((e) => !checkUndefinedNullOrEmpty(e)),
            links: [!checkUndefinedNullOrEmpty(meta?.canonical) ? { rel: 'canonical', href: meta?.canonical } : undefined]?.filter((e) => !checkUndefinedNullOrEmpty(e)),
            openGraph: {
                'description': meta?.desc,
                'image': meta?.image,
                'image:alt': meta?.imageAlt,
                'locale': getMetaLocales(settings?.general?.language),
                'tags': meta?.tags,
                'title': meta?.title,
                'type': 'website',
                'url': meta?.url,
            },
            twitter: !settings?.general?.ahdWeb
                ? {
                      card: 'summary_large_image',
                      creator: '@handle',
                      site: '@site',
                      title: meta?.title,
                  }
                : undefined,
        },
        [meta]
    );

    useEffect(() => {
        const heading = document?.querySelector('h1');
        const headingEdited = !settings?.fahrzeugsuche?.hideHeading || settings?.fahrzeugsuche?.originalH1 !== heading?.innerText;
        if (
            module === Modules.Fahrzeugsuche &&
            settings?.fahrzeugsuche?.hideHeading === true &&
            !checkUndefinedNullOrEmpty(meta?.titleRaw) &&
            (type === 'car' || headingEdited === true || (type === 'list' && props?.isFiltered === true))
        ) {
            if (!checkUndefinedOrNull(heading)) {
                clearTimeout(timerH1);

                if (type === 'car' && props?.loading === true) {
                    timerH1 = setTimeout(() => {
                        heading.innerText = meta?.titleRaw;
                        clearTimeout(timerH1);
                    }, 1500);
                } else {
                    heading.innerText = meta?.titleRaw;
                }
            }
        }

        if (settings?.fahrzeugsuche?.hideHeading === true && headingEdited === true && type === 'list' && props?.isFiltered === false) {
            heading.innerText = settings?.fahrzeugsuche?.originalH1;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta?.titleRaw, settings?.general?.ahdWeb]);

    return (
        <>
            {type === 'car' && !props?.loading && !checkUndefinedNullOrEmpty(props?.car) && (
                <JsonLd
                    item={
                        /* props?.car?.kategorie === CarKategorie.MOTORRAD
                        ? ldJsonMotorcycle(props?.car, meta)
                        : props?.car?.kategorie === CarKategorie.BUS
                            ? ldJsonBus(props?.car, meta)
                            :  */ ldJsonCar(props?.car, meta)
                    }
                />
            )}
        </>
    );
}
